import { useCallback, useEffect, useState } from "react";
import { UserCheck, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/strings";
import InviteUserDialog from "@/components/users/InviteUserDialog";
import RolesCatalog from "@/components/users/RolesCatalog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

export default function UsersRoles() {
  const { companyId, can } = useTenant();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const canManage = can("users:manage");

  const load = useCallback(async () => {
    if (!companyId || !can("users:read")) return;
    try {
      const [{ data: u }, { data: r }] = await Promise.all([
        api.get(`/companies/${companyId}/users`),
        api.get("/roles"),
      ]);
      setUsers(u);
      setRoles(r);
    } catch (err) {
      toast.error("Errore caricamento", { description: formatApiError(err) });
    }
  }, [companyId, can]);

  useEffect(() => { load(); }, [load]);

  const changeRole = async (u, newRole) => {
    try {
      await api.put(`/companies/${companyId}/users/${u.user_id}`, { role: newRole });
      toast.success("Ruolo aggiornato", { description: `${u.full_name}: ${ROLE_LABELS[newRole]}` });
      load();
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/companies/${companyId}/users/${removeTarget.user_id}`);
      toast.success("Utente rimosso", { description: removeTarget.email });
      setRemoveTarget(null);
      load();
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  if (!can("users:read")) {
    return (
      <div className="max-w-xl" data-testid="users-forbidden">
        <Card className="border-slate-200"><CardContent className="p-6 text-sm text-slate-600">
          Il tuo ruolo non consente la visualizzazione degli utenti aziendali.
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-roles-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
            <UserCheck className="w-7 h-7 text-green-700" aria-hidden="true" />
            Utenti e Ruoli
          </h1>
          <p className="text-sm text-slate-600 mt-1">Gestisci gli utenti aziendali e i loro permessi.</p>
        </div>
        {canManage && (
          <Button className="bg-green-700 hover:bg-green-800" onClick={() => setInviteOpen(true)} data-testid="invite-user-button">
            <Plus className="w-4 h-4 mr-2" aria-hidden="true" /> Aggiungi utente
          </Button>
        )}
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table data-testid="users-table">
            <TableHeader>
              <TableRow>
                <TableHead>Utente</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Ruolo</TableHead>
                {canManage && <TableHead className="text-right">Azioni</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.user_id} data-testid={`user-row-${u.email}`}>
                  <TableCell className="font-medium">{u.full_name}</TableCell>
                  <TableCell className="text-slate-600">{u.email}</TableCell>
                  <TableCell>
                    {canManage && u.user_id !== user?.id ? (
                      <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                        <SelectTrigger className="w-[180px] h-8" data-testid={`role-select-${u.email}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {roles.map((r) => (
                            <SelectItem key={r.role} value={r.role} data-testid={`role-option-${r.role}`}>{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge variant="outline" className="bg-slate-50">{ROLE_LABELS[u.role] || u.role}</Badge>
                    )}
                  </TableCell>
                  {canManage && (
                    <TableCell className="text-right">
                      {u.user_id !== user?.id && (
                        <Button size="sm" variant="ghost" className="text-red-700"
                          onClick={() => setRemoveTarget(u)} data-testid={`remove-user-${u.email}`}>
                          <Trash2 className="w-4 h-4 mr-1" aria-hidden="true" /> Rimuovi
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <RolesCatalog roles={roles} />

      <InviteUserDialog open={inviteOpen} onOpenChange={setInviteOpen}
        companyId={companyId} roles={roles} onAdded={load} />

      <AlertDialog open={!!removeTarget} onOpenChange={() => setRemoveTarget(null)}>
        <AlertDialogContent data-testid="remove-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Rimuovere l'utente?</AlertDialogTitle>
            <AlertDialogDescription>
              {removeTarget?.full_name} ({removeTarget?.email}) non potrà più accedere ai dati di questa azienda.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="remove-user-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-700 hover:bg-red-800" data-testid="remove-user-confirm">
              Rimuovi
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
